import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import StadiumIcon from "@mui/icons-material/Stadium";
import { Box, Button, Card, CardContent, CardHeader, IconButton, Stack, Step, StepLabel, Stepper, Tooltip, Typography, useMediaQuery, useTheme, } from "@mui/material";
import dayjs from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useRetrieveDivisionsBySeasonId } from "../api/DivisionApiProvider";
import { useRetrieveLeague } from "../api/LeagueApiProvider";
import { useListLeagueHomeVenues } from "../api/LeagueVenueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { useCancelSeason, useDeleteSeason, useGenerateFixtures, useRetrieveSeason, useRetrieveSeasonConfig, useUpdateSeasonConfig, } from "../api/SeasonApiProvider";
import { UserContext } from "../auth/UserProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import SuccessSnackbar from "../utils/SuccessSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
import { ActiveSeason } from "./ActiveSeason";
import { DivisionManagement } from "./DivisionManagement";
import { ExclusionsStep } from "./ExclusionsStep";
import { FinalizeStep } from "./FinalizeStep";
import { FormatRoundsStep } from "./FormatRoundsStep";
import { MatchTimingStep } from "./MatchTimingStep";
import { ScheduleWindowStep, createDefaultDays } from "./ScheduleWindowStep";
import { SeasonStatusChip } from "./SeasonStatusChip";
import { VenuesStep } from "./VenuesStep";
const SETUP_STEPS = [
    {
        label: "Teams & Divisions",
        description: "Assign teams to divisions",
        icon: _jsx(GroupsIcon, {}),
    },
    {
        label: "Format & Rounds",
        description: "Season dates and match format",
        icon: _jsx(SettingsIcon, {}),
    },
    {
        label: "Venues",
        description: "Set up match locations",
        icon: _jsx(StadiumIcon, {}),
    },
    {
        label: "Match Timing",
        description: "Duration and rest periods",
        icon: _jsx(ScheduleIcon, {}),
    },
    {
        label: "Pitch Availability",
        description: "Days and times for each pitch",
        icon: _jsx(CalendarMonthIcon, {}),
    },
    {
        label: "Exclusions",
        description: "Holidays and blackouts",
        icon: _jsx(EventBusyIcon, {}),
    },
    {
        label: "Finalize",
        description: "Generate fixtures",
        icon: _jsx(CheckCircleIcon, {}),
    },
];
export const Season = () => {
    const { id } = useNumericParams();
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [activeStep, setActiveStep] = useState(0);
    // On mobile the stepper scrolls horizontally; keep the active step in view so
    // the user isn't left looking at earlier steps after advancing.
    const activeStepRef = useRef(null);
    useEffect(() => {
        activeStepRef.current?.scrollIntoView?.({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
        });
    }, [activeStep]);
    // Update both state and URL when changing steps
    const goToStep = (step) => {
        setActiveStep(step);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("step", String(step));
            return next;
        }, { replace: true });
    };
    const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const [successSnackbarMessage, setSuccessSnackbarMessage] = useState("");
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("");
    const [step1HasUnsavedChanges, setStep1HasUnsavedChanges] = useState(false);
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    // Fixture config form - single source of truth for all steps
    const formMethods = useForm({
        defaultValues: {},
    });
    const fixtureConfig = formMethods.watch();
    const { data: season, isLoading, error } = useRetrieveSeason({ id });
    const { data: league, isLoading: leagueLoading } = useRetrieveLeague({
        id: season?.league,
        enabled: !!season?.league,
    });
    const { data: divisions, isLoading: divisionsLoading } = useRetrieveDivisionsBySeasonId({
        seasonId: season?.id,
        enabled: !!season?.id,
    });
    const { data: permissions, isLoading: permissionsLoading } = useRetrieveUserLeaguePermissions({
        leagueId: season?.league,
    });
    const { canModify = false } = permissions || {};
    const { data: leagueVenues, isLoading: leagueVenuesLoading } = useListLeagueHomeVenues({
        leagueId: season?.league,
        enabled: !!season?.league && !!user,
    });
    const { data: seasonConfig, isLoading: seasonConfigLoading, error: seasonConfigError, } = useRetrieveSeasonConfig({
        seasonId: season?.id,
        enabled: !!season?.id && canModify,
    });
    const cancelMutation = useCancelSeason();
    const updateConfigMutation = useUpdateSeasonConfig({
        seasonId: id,
    });
    const deleteMutation = useDeleteSeason({
        onMutate: () => setDeleting(true),
        onSuccess: () => navigate(`/leagues/${season?.league}/`),
        onError: () => setDeleting(false),
    });
    const generateMutation = useGenerateFixtures({
        seasonId: id,
        onSuccess: (response) => {
            const { totalMatches } = response.data;
            setSuccessSnackbarMessage(totalMatches === 0
                ? "Season activated! You can now create matches manually."
                : `Successfully generated ${totalMatches} fixtures!`);
            setSuccessSnackbarOpen(true);
            // Navigate to the season page (will show active state)
            navigate(`/leagues/${season?.league}/seasons/${id}`);
        },
        onError: (error) => {
            const axiosError = error;
            const message = axiosError.response?.data?.error ||
                "Failed to generate fixtures. Please try again.";
            setErrorSnackbarMessage(message);
            setErrorSnackbarOpen(true);
        },
    });
    // Initialize fixtureConfig form from loaded seasonConfig
    useEffect(() => {
        if (!seasonConfig || isFormInitialized)
            return;
        const newConfig = {};
        // Format & Rounds (now includes dates)
        if (seasonConfig.startDate || seasonConfig.endMode) {
            // Map endMode from API to scheduleMode
            const scheduleMode = seasonConfig.endMode === "date"
                ? "fillDates"
                : seasonConfig.endMode === "manual"
                    ? "manual"
                    : "roundRobin";
            newConfig.formatRounds = {
                scheduleMode,
                startDate: seasonConfig.startDate
                    ? new Date(seasonConfig.startDate)
                    : new Date(),
                endDate: seasonConfig.endDate ? new Date(seasonConfig.endDate) : null,
                matchesPerPairing: seasonConfig.matchesPerPairing ?? 1,
                randomizeOrder: seasonConfig.randomizeOrder ?? false,
                gameWeekStartDay: (seasonConfig.gameWeekStartDay ?? 0),
                divisionPlayoffs: seasonConfig.divisionPlayoffs?.map((dp) => ({
                    divisionId: dp.division,
                    enabled: dp.enabled ?? false,
                    teamCount: dp.teamCount,
                })) ?? [],
            };
        }
        // Venues - API returns venues with nested pitches
        if (seasonConfig.venueMode) {
            const venueMode = seasonConfig.venueMode === "home_away" ? "homeAway" : "neutral";
            // Extract IDs and pitch info from the nested structure
            const selectedVenuesData = seasonConfig.selectedVenues ?? [];
            const selectedVenueIds = selectedVenuesData.map((v) => v.id);
            const selectedPitchIds = [];
            const selectedPitches = [];
            selectedVenuesData.forEach((venue) => {
                venue.pitches.forEach((pitch) => {
                    selectedPitchIds.push(pitch.id);
                    selectedPitches.push({
                        pitchId: pitch.id,
                        pitchName: pitch.name,
                        venueId: venue.id,
                        venueName: venue.name,
                    });
                });
            });
            newConfig.venues = {
                venueMode,
                selectedVenueIds,
                selectedPitchIds,
                selectedPitches,
            };
        }
        // Match Timing
        if (seasonConfig.matchDuration !== null &&
            seasonConfig.matchDuration !== undefined) {
            newConfig.matchTiming = {
                matchDuration: seasonConfig.matchDuration,
                halfTimeBreak: seasonConfig.halfTimeBreak ?? 0,
                warmupTime: seasonConfig.warmupTime ?? 5,
                timezone: seasonConfig.timezone ||
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
        }
        // Schedule Window (pitch schedules only)
        if (seasonConfig.pitchAvailability &&
            seasonConfig.pitchAvailability.length > 0) {
            const dayKeys = [
                "monday",
                "tuesday",
                "wednesday",
                "thursday",
                "friday",
                "saturday",
                "sunday",
            ];
            newConfig.scheduleWindow = {
                pitchSchedules: seasonConfig.pitchAvailability.reduce((acc, pa) => {
                    // Use -venueId for venue-only entries (no pitch), matching stepContext convention
                    const pitchId = pa.pitch ?? (pa.venue ? -pa.venue : 0);
                    const venueId = pa.venue ?? 0;
                    // Group by pitch
                    const existing = acc.find((ps) => ps.pitchId === pitchId);
                    const dayKey = dayKeys[pa.dayOfWeek];
                    if (existing) {
                        if (dayKey) {
                            existing.days[dayKey] = {
                                enabled: true,
                                timeWindows: [
                                    { startTime: pa.startTime, endTime: pa.endTime },
                                ],
                            };
                        }
                    }
                    else {
                        const days = createDefaultDays();
                        if (dayKey) {
                            days[dayKey] = {
                                enabled: true,
                                timeWindows: [
                                    { startTime: pa.startTime, endTime: pa.endTime },
                                ],
                            };
                        }
                        acc.push({
                            pitchId: pitchId,
                            pitchName: pitchId === 0
                                ? "All Venues"
                                : pitchId < 0
                                    ? `Venue ${venueId}`
                                    : `Pitch ${pitchId}`,
                            venueId: venueId,
                            venueName: "",
                            days,
                        });
                    }
                    return acc;
                }, []),
            };
        }
        // Exclusions
        if (seasonConfig.exclusions && seasonConfig.exclusions.length > 0) {
            newConfig.exclusions = {
                excludedDates: [],
                blackoutPeriods: seasonConfig.exclusions.map((e) => ({
                    startDate: new Date(e.startDate),
                    endDate: new Date(e.endDate),
                    reason: e.reason ?? "",
                })),
                skipHolidays: false,
                countryCode: "US",
            };
        }
        formMethods.reset(newConfig);
        setIsFormInitialized(true);
    }, [seasonConfig, isFormInitialized, formMethods]);
    // Auto-advance to the first incomplete step when page loads (based on backend data only)
    useEffect(() => {
        // Don't auto-advance if we've already done it
        if (hasAutoAdvanced)
            return;
        // Wait for all data to load
        if (isLoading || divisionsLoading || seasonConfigLoading)
            return;
        // Mark as auto-advanced FIRST to prevent re-runs
        setHasAutoAdvanced(true);
        // If a step was explicitly requested via query param (e.g. returnTo from team creation),
        // honor it instead of auto-advancing
        const stepParam = searchParams.get("step");
        if (stepParam !== null) {
            const requestedStep = parseInt(stepParam, 10);
            if (!isNaN(requestedStep) &&
                requestedStep >= 0 &&
                requestedStep < SETUP_STEPS.length) {
                setActiveStep(requestedStep);
                return;
            }
        }
        // Find the first incomplete step based on BACKEND data (seasonConfig), not form state
        const checkStepComplete = (stepIndex) => {
            switch (stepIndex) {
                case 0: {
                    // Step 1: Teams & Divisions
                    if (!divisions || divisions.length === 0)
                        return false;
                    return divisions.every((d) => d.teams && d.teams.length >= 2);
                }
                case 1: {
                    // Step 2: Format & Rounds - check backend seasonConfig
                    if (seasonConfig?.endMode === "manual")
                        return true;
                    if (!seasonConfig?.startDate)
                        return false;
                    if (seasonConfig?.endMode === "date" && !seasonConfig?.endDate)
                        return false;
                    if (seasonConfig?.endMode !== "date" &&
                        !seasonConfig?.matchesPerPairing)
                        return false;
                    return true;
                }
                case 2: {
                    // Step 3: Venues - auto-complete for manual mode
                    if (seasonConfig?.endMode === "manual")
                        return true;
                    if (!seasonConfig?.venueMode)
                        return false;
                    if (seasonConfig.venueMode === "home_away")
                        return true;
                    return (seasonConfig.selectedVenues?.length ?? 0) > 0;
                }
                case 3: {
                    // Step 4: Match Timing - auto-complete for manual mode
                    if (seasonConfig?.endMode === "manual")
                        return true;
                    return (seasonConfig?.matchDuration !== undefined &&
                        seasonConfig?.matchDuration !== null);
                }
                case 4: {
                    // Step 5: Schedule Window - auto-complete for manual mode
                    if (seasonConfig?.endMode === "manual")
                        return true;
                    return (seasonConfig?.pitchAvailability?.length ?? 0) > 0;
                }
                case 5: {
                    // Step 6: Exclusions - auto-complete for manual mode
                    if (seasonConfig?.endMode === "manual")
                        return true;
                    return checkStepComplete(4);
                }
                default:
                    return false;
            }
        };
        // Find first incomplete step
        let targetStep = 0;
        for (let i = 0; i < SETUP_STEPS.length; i++) {
            if (!checkStepComplete(i)) {
                targetStep = i;
                break;
            }
            // If all steps are complete, go to the finalize step
            if (i === SETUP_STEPS.length - 1) {
                targetStep = SETUP_STEPS.length - 1;
            }
        }
        goToStep(targetStep);
    }, [
        hasAutoAdvanced,
        isLoading,
        divisionsLoading,
        seasonConfigLoading,
        divisions,
        seasonConfig,
        searchParams,
    ]);
    const canModifyStructure = (status) => {
        return status === "draft";
    };
    // Check if Step 1 (Teams & Divisions) is complete
    const isStep1Complete = () => {
        if (!divisions || divisions.length === 0)
            return false;
        return divisions.every((division) => division.teams && division.teams.length >= 2);
    };
    // Check if Step 2 (Format & Rounds) is complete (based on SAVED backend data)
    const isStep2Complete = () => {
        if (seasonConfig?.endMode === "manual")
            return true;
        if (!seasonConfig?.startDate)
            return false;
        if (seasonConfig?.endMode === "date" && !seasonConfig?.endDate)
            return false;
        if (seasonConfig?.endMode !== "date" && !seasonConfig?.matchesPerPairing)
            return false;
        return true;
    };
    // Check if Step 2 form is valid (for enabling Next button)
    const isStep2Valid = () => {
        const formatRounds = fixtureConfig.formatRounds;
        if (!formatRounds)
            return false;
        if (formatRounds.scheduleMode === "manual")
            return true;
        if (!formatRounds.startDate)
            return false;
        if (formatRounds.scheduleMode === "fillDates" && !formatRounds.endDate)
            return false;
        if (formatRounds.scheduleMode === "roundRobin" &&
            !formatRounds.matchesPerPairing)
            return false;
        return true;
    };
    // Check if Step 3 (Venues) is complete (based on SAVED backend data)
    const isStep3Complete = () => {
        if (fixtureConfig.formatRounds?.scheduleMode === "manual")
            return true;
        if (!seasonConfig?.venueMode)
            return false;
        if (seasonConfig.venueMode === "home_away")
            return true;
        return (seasonConfig.selectedVenues?.length ?? 0) > 0;
    };
    // Check if Step 3 form is valid (for enabling Next button)
    const isStep3Valid = () => {
        if (fixtureConfig.formatRounds?.scheduleMode === "manual")
            return true;
        const venues = fixtureConfig.venues;
        if (!venues || !venues.venueMode)
            return false;
        if (venues.venueMode === "homeAway")
            return true;
        return (venues.selectedVenueIds?.length ?? 0) > 0;
    };
    // Check if Step 4 (Match Timing) is complete (based on SAVED backend data)
    const isStep4Complete = () => {
        if (fixtureConfig.formatRounds?.scheduleMode === "manual")
            return true;
        return (seasonConfig?.matchDuration !== undefined &&
            seasonConfig?.matchDuration !== null);
    };
    // Check if Step 4 form is valid (for enabling Next button)
    const isStep4Valid = () => {
        if (fixtureConfig.formatRounds?.scheduleMode === "manual")
            return true;
        return fixtureConfig.matchTiming?.matchDuration !== undefined;
    };
    // Check if Step 5 (Schedule Window) is complete (based on SAVED backend data)
    const isStep5Complete = () => {
        if (fixtureConfig.formatRounds?.scheduleMode === "manual")
            return true;
        return (seasonConfig?.pitchAvailability?.length ?? 0) > 0;
    };
    // Check if Step 5 form is valid (for enabling Next button)
    const isStep5Valid = () => {
        if (fixtureConfig.formatRounds?.scheduleMode === "manual")
            return true;
        const scheduleWindow = fixtureConfig.scheduleWindow;
        if (!scheduleWindow)
            return false;
        return scheduleWindow.pitchSchedules.some((ps) => Object.values(ps.days).some((day) => day.enabled));
    };
    // Check if Step 6 (Exclusions) is complete
    const isStep6Complete = () => {
        if (fixtureConfig.formatRounds?.scheduleMode === "manual")
            return true;
        // Exclusions step is always valid (optional to add exclusions)
        return isStep5Complete(); // Just needs previous step to be complete
    };
    // Check if a specific step is complete (for stepper checkmarks - based on SAVED data)
    const isStepComplete = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                return isStep1Complete();
            case 1:
                return isStep2Complete();
            case 2:
                return isStep3Complete();
            case 3:
                return isStep4Complete();
            case 4:
                return isStep5Complete();
            case 5:
                return isStep6Complete();
            default:
                return false;
        }
    };
    // Check if user can proceed from a step (for enabling Next button - based on FORM data)
    const canProceedFromStep = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                return isStep1Complete(); // Step 1 has no form, uses divisions directly
            case 1:
                return isStep2Valid();
            case 2:
                return isStep3Valid();
            case 3:
                return isStep4Valid();
            case 4:
                return isStep5Valid();
            case 5:
                return true; // Exclusions are optional
            default:
                return false;
        }
    };
    // Determine if a step can be navigated to
    const canNavigateToStep = (stepIndex) => {
        // Can always go back to previous steps
        if (stepIndex <= activeStep)
            return true;
        // Can only go forward if all previous steps are complete
        for (let i = 0; i < stepIndex; i++) {
            if (!isStepComplete(i))
                return false;
        }
        // Additional check for step 1: no unsaved changes
        if (activeStep === 0 && step1HasUnsavedChanges)
            return false;
        return true;
    };
    const handleStepClick = (stepIndex) => {
        if (canNavigateToStep(stepIndex)) {
            goToStep(stepIndex);
        }
    };
    // Build config payload for current step
    const buildConfigPayload = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload = {};
        // Always send timezone so it's set from the first config save
        payload.timezone =
            fixtureConfig.matchTiming?.timezone ||
                Intl.DateTimeFormat().resolvedOptions().timeZone;
        switch (activeStep) {
            case 1: {
                // Format & Rounds (now includes dates)
                const fr = fixtureConfig.formatRounds;
                if (fr) {
                    payload.startDate = fr.startDate?.toISOString().split("T")[0] ?? null;
                    // Map scheduleMode to endMode for API
                    if (fr.scheduleMode === "manual") {
                        payload.endMode = "manual";
                        payload.endDate = null;
                    }
                    else if (fr.scheduleMode === "fillDates") {
                        payload.endMode = "date";
                        payload.endDate = fr.endDate?.toISOString().split("T")[0] ?? null;
                    }
                    else {
                        payload.endMode = "rounds";
                        payload.endDate = null;
                        payload.matchesPerPairing = fr.matchesPerPairing;
                    }
                    if (fr.scheduleMode !== "manual") {
                        payload.randomizeOrder = fr.randomizeOrder;
                        payload.gameWeekStartDay = fr.gameWeekStartDay;
                        payload.divisionPlayoffs = fr.divisionPlayoffs
                            .filter((dp) => dp.enabled)
                            .map((dp) => ({
                            division: dp.divisionId,
                            enabled: dp.enabled,
                            teamCount: dp.teamCount,
                        }));
                    }
                }
                break;
            }
            case 2: {
                // Venues
                const v = fixtureConfig.venues;
                if (v) {
                    if (v.venueMode) {
                        payload.venueMode =
                            v.venueMode === "homeAway" ? "home_away" : "neutral";
                    }
                    payload.selectedVenueIds = v.selectedVenueIds;
                    payload.selectedPitchIds = v.selectedPitchIds;
                }
                break;
            }
            case 3: {
                // Match Timing
                const mt = fixtureConfig.matchTiming;
                if (mt) {
                    payload.matchDuration = mt.matchDuration;
                    payload.halfTimeBreak = mt.halfTimeBreak;
                    payload.warmupTime = mt.warmupTime;
                }
                break;
            }
            case 4: {
                // Schedule Window (pitch schedules only, dates moved to step 1)
                const sw = fixtureConfig.scheduleWindow;
                if (sw) {
                    // Convert pitch schedules to pitch_availability format
                    const pitchAvailability = [];
                    const dayKeyToNumber = {
                        monday: 0,
                        tuesday: 1,
                        wednesday: 2,
                        thursday: 3,
                        friday: 4,
                        saturday: 5,
                        sunday: 6,
                    };
                    sw.pitchSchedules.forEach((ps) => {
                        Object.entries(ps.days).forEach(([dayKey, dayConfig]) => {
                            if (dayConfig.enabled && dayConfig.timeWindows[0]) {
                                const entry = {
                                    dayOfWeek: dayKeyToNumber[dayKey] ?? 0,
                                    startTime: dayConfig.timeWindows[0].startTime,
                                    endTime: dayConfig.timeWindows[0].endTime,
                                };
                                // Venue-only entries have negative pitchId (placeholder)
                                entry.venue = ps.venueId > 0 ? ps.venueId : undefined;
                                entry.pitch = ps.pitchId > 0 ? ps.pitchId : undefined;
                                pitchAvailability.push(entry);
                            }
                        });
                    });
                    payload.pitchAvailability = pitchAvailability;
                }
                break;
            }
            case 5: {
                // Exclusions
                const ex = fixtureConfig.exclusions;
                if (ex) {
                    const exclusions = [];
                    // Add blackout periods
                    ex.blackoutPeriods.forEach((bp) => {
                        exclusions.push({
                            startDate: bp.startDate.toISOString().split("T")[0] ?? "",
                            endDate: bp.endDate.toISOString().split("T")[0] ?? "",
                            reason: bp.reason,
                        });
                    });
                    // Add excluded dates as single-day exclusions
                    ex.excludedDates.forEach((ed) => {
                        const dateStr = ed.date.toISOString().split("T")[0] ?? "";
                        exclusions.push({
                            startDate: dateStr,
                            endDate: dateStr,
                            reason: ed.reason,
                        });
                    });
                    payload.exclusions = exclusions;
                }
                break;
            }
            default:
                break;
        }
        return payload;
    };
    const handleNext = () => {
        if (activeStep < SETUP_STEPS.length - 1) {
            const isManual = fixtureConfig.formatRounds?.scheduleMode === "manual";
            // Steps 1-5 save config (step 0 is team assignment, step 6 is finalize)
            if (activeStep >= 1 && activeStep <= 5) {
                const payload = buildConfigPayload();
                // Only patch if there's something to save
                if (Object.keys(payload).length > 0) {
                    updateConfigMutation.mutate(payload, {
                        onSuccess: () => {
                            // Manual mode: skip from step 1 directly to Finalize (step 6)
                            if (isManual && activeStep === 1) {
                                goToStep(6);
                            }
                            else {
                                goToStep(activeStep + 1);
                            }
                        },
                    });
                    return;
                }
            }
            // Manual mode: skip from step 1 directly to Finalize (step 6)
            if (isManual && activeStep === 1) {
                goToStep(6);
            }
            else {
                goToStep(activeStep + 1);
            }
        }
    };
    const handleBack = () => {
        if (activeStep > 0) {
            // Manual mode: jump back from Finalize (step 6) to Format (step 1)
            if (fixtureConfig.formatRounds?.scheduleMode === "manual" &&
                activeStep === 6) {
                goToStep(1);
            }
            else {
                goToStep(activeStep - 1);
            }
        }
    };
    // Build shared context for all step components
    const stepContext = React.useMemo(() => {
        const teamCount = divisions?.reduce((sum, d) => sum + (d.teams?.length || 0), 0) || 0;
        const gamesPerRound = Math.floor(teamCount / 2);
        const matchesPerPairing = fixtureConfig.formatRounds?.matchesPerPairing || 1;
        const totalRounds = matchesPerPairing * (teamCount > 0 ? teamCount - 1 : 0);
        const pitchCount = fixtureConfig.venues?.selectedPitchIds?.length || 0;
        // Build pitch info from selected pitches/venues
        const selectedPitchesFromConfig = fixtureConfig.venues?.selectedPitches || [];
        const venueIds = fixtureConfig.venues?.selectedVenueIds || [];
        const pitches = [];
        const pitchesByVenue = new Map();
        selectedPitchesFromConfig.forEach((p) => {
            if (!pitchesByVenue.has(p.venueId)) {
                pitchesByVenue.set(p.venueId, []);
            }
            pitchesByVenue.get(p.venueId).push(p);
        });
        venueIds.forEach((venueId) => {
            const venue = leagueVenues?.find((lv) => lv.venue?.id === venueId);
            const venueName = venue?.venue?.name || `Venue ${venueId}`;
            const venuePitches = pitchesByVenue.get(venueId);
            if (venuePitches && venuePitches.length > 0) {
                venuePitches.forEach((p) => {
                    pitches.push({
                        id: p.pitchId,
                        name: p.pitchName,
                        venueId,
                        venueName,
                    });
                });
            }
            else {
                pitches.push({ id: -venueId, name: venueName, venueId, venueName: "" });
            }
        });
        // Calculate estimated end date for rounds mode
        const formatRounds = fixtureConfig.formatRounds;
        const scheduleWindow = fixtureConfig.scheduleWindow;
        const slotDuration = fixtureConfig.matchTiming
            ? fixtureConfig.matchTiming.matchDuration +
                fixtureConfig.matchTiming.halfTimeBreak +
                fixtureConfig.matchTiming.warmupTime
            : 45;
        const totalSlotsPerWeek = scheduleWindow?.pitchSchedules?.reduce((sum, ps) => {
            return (sum +
                Object.values(ps.days).reduce((daySum, day) => {
                    if (!day.enabled || !day.timeWindows[0])
                        return daySum;
                    const tw = day.timeWindows[0];
                    const startParts = tw.startTime.split(":").map(Number);
                    const endParts = tw.endTime.split(":").map(Number);
                    const minutes = (endParts[0] ?? 0) * 60 +
                        (endParts[1] ?? 0) -
                        ((startParts[0] ?? 0) * 60 + (startParts[1] ?? 0));
                    return daySum + Math.floor(minutes / slotDuration);
                }, 0));
        }, 0) || 0;
        const totalGames = gamesPerRound * totalRounds;
        const weeksNeeded = totalSlotsPerWeek > 0 ? Math.ceil(totalGames / totalSlotsPerWeek) : 12;
        const estimatedEndDate = formatRounds?.startDate && formatRounds?.scheduleMode === "roundRobin"
            ? new Date(formatRounds.startDate.getTime() +
                weeksNeeded * 7 * 24 * 60 * 60 * 1000)
            : null;
        return {
            divisions: divisions || [],
            leagueVenues: leagueVenues || [],
            leagueId: season?.league,
            seasonId: season?.id,
            teamCount,
            gamesPerRound,
            totalRounds,
            pitchCount,
            pitches,
            startDate: formatRounds?.startDate,
            endDate: formatRounds?.endDate,
            endMode: (formatRounds?.scheduleMode === "fillDates"
                ? "date"
                : formatRounds?.scheduleMode === "manual"
                    ? "manual"
                    : "rounds"),
            estimatedEndDate,
            gameWeekStartDay: formatRounds?.gameWeekStartDay ?? 0,
        };
    }, [divisions, leagueVenues, season?.league, fixtureConfig]);
    // Render step content
    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (_jsx(DivisionManagement, { season: season, canModify: permissions?.canModify || false, showActions: true, onHasChangesChange: setStep1HasUnsavedChanges, onDeleteSuccess: (message) => {
                        setSuccessSnackbarMessage(message);
                        setSuccessSnackbarOpen(true);
                    }, onDeleteError: (message) => {
                        setErrorSnackbarMessage(message);
                        setErrorSnackbarOpen(true);
                    } }));
            case 1:
                return _jsx(FormatRoundsStep, { context: stepContext });
            case 2:
                return _jsx(VenuesStep, { context: stepContext });
            case 3:
                return _jsx(MatchTimingStep, { context: stepContext });
            case 4:
                return _jsx(ScheduleWindowStep, { context: stepContext });
            case 5:
                return _jsx(ExclusionsStep, { context: stepContext });
            case 6: {
                const teamCount = divisions?.reduce((sum, d) => sum + (d.teams?.length || 0), 0) || 0;
                const divisionCount = divisions?.length || 0;
                const handleGenerate = () => {
                    generateMutation.mutate();
                };
                return (_jsx(FinalizeStep, { teamCount: teamCount, divisionCount: divisionCount, formatRounds: fixtureConfig.formatRounds, venues: fixtureConfig.venues, matchTiming: fixtureConfig.matchTiming, scheduleWindow: fixtureConfig.scheduleWindow, exclusions: fixtureConfig.exclusions, onGenerate: handleGenerate, isGenerating: generateMutation.isLoading, generateError: null }));
            }
            default: {
                const step = SETUP_STEPS[activeStep];
                return (_jsxs(Box, { sx: { p: 4, textAlign: "center" }, children: [_jsx(Typography, { variant: "h6", color: "text.secondary", gutterBottom: true, children: step?.label }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Coming soon..." })] }));
            }
        }
    };
    if (!id) {
        return _jsx(ErrorView, { error: new Error("Missing season id") });
    }
    // If retrieving the season is forbidden, show Forbidden
    if (error?.response?.status === 403) {
        return _jsx(Forbidden, {});
    }
    if (isLoading ||
        deleting ||
        permissionsLoading ||
        leagueLoading ||
        divisionsLoading ||
        (leagueVenuesLoading && !!user) ||
        (seasonConfigLoading && canModify)) {
        return _jsx(Loading, {});
    }
    if (error || !season || (canModify && seasonConfigError)) {
        return _jsx(ErrorView, { error: error || new Error("Failed to load season") });
    }
    if (season.status !== "draft") {
        return (_jsx(ActiveSeason, { season: season, league: league, permissions: permissions, onCancel: () => cancelMutation.mutate({ id: season.id }), isCancelling: cancelMutation.isLoading }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(SuccessSnackbar, { open: successSnackbarOpen, message: successSnackbarMessage, onClose: () => {
                    setSuccessSnackbarOpen(false);
                    setSuccessSnackbarMessage("");
                } }), _jsx(ErrorSnackbar, { open: deleteMutation.isError ||
                    updateConfigMutation.isError ||
                    errorSnackbarOpen, message: deleteMutation.isError
                    ? "Failed to delete season. Please try again."
                    : updateConfigMutation.isError
                        ? "Failed to save configuration. Please try again."
                        : errorSnackbarMessage, onClose: () => {
                    setErrorSnackbarOpen(false);
                    setErrorSnackbarMessage("");
                    updateConfigMutation.reset();
                } }), _jsx(DeleteDialog, { open: showDelete, title: `Delete ${season.name}?`, onCancel: () => setShowDelete(false), onConfirm: () => deleteMutation.mutate({
                    id: season.id,
                    leagueId: season.league,
                }) }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Stack, { spacing: 1, children: [league && (_jsx(Link, { to: `/leagues/${season.league}/`, style: { textDecoration: "none" }, children: _jsx(Typography, { variant: "body2", color: "primary.main", children: league.name }) })), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [_jsx(Typography, { variant: "h5", children: season.name }), _jsx(SeasonStatusChip, { season: season })] })] }), subheader: fixtureConfig.formatRounds?.startDate ? (_jsxs(Typography, { variant: "caption", fontStyle: "italic", children: ["Starts", " ", dayjs(fixtureConfig.formatRounds.startDate).format("MMM D, YYYY")] })) : undefined, action: _jsx(Stack, { direction: "row", spacing: 1, children: permissions?.canModify && canModifyStructure(season.status) && (_jsxs(_Fragment, { children: [_jsx(Link, { to: `/leagues/${season.league}/seasons/${season.id}/edit`, children: _jsx(IconButton, { "aria-label": "edit", children: _jsx(EditIcon, {}) }) }), _jsx(IconButton, { "aria-label": "delete", onClick: () => setShowDelete(true), children: _jsx(DeleteIcon, {}) })] })) }) }), season.description && (_jsx(CardContent, { children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: season.description }) }))] }), season.status === "draft" && (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Box, { sx: { width: "100%", mb: 4, overflowX: "auto" }, children: _jsx(Stepper, { activeStep: activeStep, alternativeLabel: true, sx: {
                                    minWidth: { xs: 600, sm: "auto" },
                                    "& .MuiStepLabel-root": {
                                        cursor: "pointer",
                                    },
                                    "& .Mui-active .MuiStepLabel-label": {
                                        fontWeight: 700,
                                        color: "primary.main",
                                    },
                                    "& .Mui-active .MuiStepIcon-root": {
                                        fontSize: "1.75rem",
                                        color: "primary.main",
                                    },
                                    "& .Mui-completed .MuiStepIcon-root": {
                                        color: "success.main",
                                    },
                                }, children: SETUP_STEPS.map((step, index) => (_jsx(Step, { completed: isStepComplete(index), ref: index === activeStep ? activeStepRef : undefined, children: _jsx(StepLabel, { onClick: () => handleStepClick(index), optional: !isMobile ? (_jsx(Typography, { variant: "caption", children: step.description })) : undefined, sx: {
                                            "& .MuiStepLabel-label": {
                                                cursor: canNavigateToStep(index)
                                                    ? "pointer"
                                                    : "default",
                                                opacity: canNavigateToStep(index) ? 1 : 0.5,
                                            },
                                        }, children: step.label }) }, step.label))) }) }), _jsx(FormProvider, { ...formMethods, children: _jsx(Box, { sx: { minHeight: 300 }, children: renderStepContent() }) }), _jsxs(Box, { sx: {
                                display: "flex",
                                justifyContent: "space-between",
                                pt: 3,
                                borderTop: 1,
                                borderColor: "divider",
                                mt: 3,
                            }, children: [_jsx(Button, { disabled: activeStep === 0, onClick: handleBack, variant: "outlined", children: "Back" }), _jsx(Stack, { direction: "row", spacing: 2, children: activeStep < SETUP_STEPS.length - 1 && (_jsx(Tooltip, { title: activeStep === 0 && step1HasUnsavedChanges
                                            ? "Save your team assignments before continuing"
                                            : !canProceedFromStep(activeStep)
                                                ? activeStep === 0
                                                    ? "All divisions need at least 2 teams"
                                                    : "Complete this step before continuing"
                                                : "", children: _jsx("span", { children: _jsx(Button, { variant: "contained", onClick: handleNext, disabled: !canProceedFromStep(activeStep) ||
                                                    (activeStep === 0 && step1HasUnsavedChanges) ||
                                                    updateConfigMutation.isLoading, children: updateConfigMutation.isLoading ? "Saving..." : "Next" }) }) })) })] })] }) }))] }));
};
